import TagsPickerVM from 'components/tags/picker/picker';
import { wccModules } from 'enums/wccModules';
import { DOMHelpers } from 'helpers/dom';
import { inject, injectable } from 'inversify';
import { Subscribable, SubscribableOrNullableValue } from 'knockout';
import { withEffect } from 'mixins/withEffect';
import { EffectsContainer } from 'mixins/withEffects';
import { Tag } from 'models/tag';
import { CollectionsHelpers } from '../../../helpers/collections';
import TopicCommentFilterVM, { TopicCommentsFilterVMConfig } from './filter';
import './tagsFilter.html';
import TopicCommentsFiltersVMTagsGroup from './tagsGroup';

const regularTagsGroupName = labels.Other;
const shiftKeyCode = 16;

export interface TopicCommentsFiltersVMTagsFilterConfig extends TopicCommentsFilterVMConfig<Array<string>> {
    tags: SubscribableOrNullableValue<Array<Tag>>
}

@injectable()
export default class TopicCommentsFiltersVMTagsFilterVM extends TopicCommentFilterVM<Array<string>> {
    tagsPickers = ko.observableArray<TagsPickerVM>();
    tagsGroups: Subscribable<Array<TopicCommentsFiltersVMTagsGroup>>

    allowMultiple = ko.observable(false);

    constructor(
        @inject(wccModules.componentConfig) config: TopicCommentsFiltersVMTagsFilterConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        super(config, info, effects, []);

        const tags = ko.flattenComputed(config.tags, []);

        this.tagsGroups = tags.mapSingle(tags => this.buildTagsGroups(tags)).sortBy(g => g.title).sortBy(g => g.id == regularTagsGroupName);

        const isReady = ko.pureComputed(() => tags().length > 0);

        //we need to sync pickers in case we don't allow to select multiple picker
        effects.register((pickers, allowMultiple) => {
            if (!allowMultiple) {
                //track every picker selection change
                return pickers.map(picker => picker.selectedTag.subscribeNotNull(() => {
                    //on change we clear other pickers so only one tag will be selected
                    pickers.forEach(otherPicker => {
                        if (otherPicker != picker)
                            otherPicker.clear();
                    });
                }));
            }
        }, [this.tagsPickers, this.allowMultiple]);

        //We need to wire all our pickers to value field
        effects.register((tagsGroups, tagsPickers) => {
            //when pickers are ready
            if (tagsPickers.length > 0 && tagsPickers.length == tagsGroups.length) {
                const selectedTagsIDs = ko.pureComputed(() => tagsPickers.reduce((result, picker) => result.concat(picker.selectedTags().map(t => <string>t.contentTagId())), <Array<string>>[]));

                return [
                    //load IDs into pickers on value field change
                    withEffect(tagIDs => {
                        if (!CollectionsHelpers.compareCollections(tagIDs, selectedTagsIDs()))
                            tagsPickers.forEach(p => p.select(tagIDs));
                    }, [this.value]),

                    //and on pickers selection change we write it into value field
                    withEffect(newTagIDs => {
                        if (!CollectionsHelpers.compareCollections(newTagIDs, this.value()))
                            this.value(newTagIDs);
                    }, [selectedTagsIDs])
                ];
            }
        }, [this.tagsGroups, this.tagsPickers]);

        effects.register([
            DOMHelpers.onNodeEvent(document.body, 'keydown', e => this.onKeyDown(<KeyboardEvent>e.originalEvent)),
            DOMHelpers.onNodeEvent(document.body, 'keyup', e => this.onKeyUp(<KeyboardEvent>e.originalEvent)),
            DOMHelpers.onNodeEvent(window, 'blur', () => this.allowMultiple(false))
        ]);

        ko.applyBindingsToNode(info.element, {
            'visible': isReady,

            'css': {
                'topic-comments-filters__tags-filter': true
            }
        });
    }

    protected override compare(left: Array<string>, right: Array<string>) {
        return CollectionsHelpers.compareCollections(left, right);
    }

    private onKeyDown(e: KeyboardEvent) {
        if (e.keyCode == shiftKeyCode)
            this.allowMultiple(true);
    }

    private onKeyUp(e: KeyboardEvent) {
        if (e.keyCode == shiftKeyCode)
            this.allowMultiple(false);
    }

    /**
     * builds groups from tags
     * @param tags tags list
     */
    private buildTagsGroups(tags: Array<Tag>) {
        return tags.reduce((groups, tag) => {
            if (tag.isComplex()) {
                //each complex tag create it's own group with root as group name

                let group = groups.find(g => g.id == tag.tagRoot());

                if (group == undefined) {
                    group = new TopicCommentsFiltersVMTagsGroup(<string>tag.tagRoot());
                    groups.push(group);
                }

                group.add(tag);
            } else {
                //regular tags go in "Other" group

                let group = groups.find(g => g.id == regularTagsGroupName);

                if (group == undefined) {
                    group = new TopicCommentsFiltersVMTagsGroup(regularTagsGroupName);
                    groups.push(group);
                }

                group.add(tag);
            }

            return groups;
        }, <Array<TopicCommentsFiltersVMTagsGroup>>[]);
    }
}